import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import type { ConsumerAppDispatch, ConsumerAppState } from './store'
import { Route } from 'next'
import { SupportedLocale } from '@afterpay/types'
import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring'
import { useRouter } from 'next/router'

/* React Hooks */

export const useAppDispatch = (): ConsumerAppDispatch => useDispatch<ConsumerAppDispatch>()

export const useAppSelector: TypedUseSelectorHook<ConsumerAppState> = useSelector

interface AppRouter {
  push: (url: Route, locale?: SupportedLocale) => void
  pushQuery: <T extends ParsedUrlQueryInput>({ pathname, query }: { pathname: Route; query: T }) => void
  replaceQuery: <T extends ParsedUrlQueryInput>({ pathname, query }: { pathname: Route; query: T }) => void
  prefetch: (url: Route) => void
  replace: (url: Route) => void
  back: () => void
  locale: SupportedLocale
  asPath: Route
  query: ParsedUrlQuery
}

export const useAppRouter = (): AppRouter => {
  const router = useRouter()
  const consumer = useAppSelector((state) => state.consumer)
  const consumerLocale = consumer?.preferredLocale ?? router?.locale

  return {
    push(url, locale) {
      router.push(url, url, { locale: locale ?? consumerLocale })
    },
    pushQuery({ pathname, query }) {
      router.push({ pathname, query }, pathname)
    },
    replaceQuery({ pathname, query }) {
      router.replace({ pathname, query }, pathname)
    },
    prefetch(url) {
      router.prefetch(url)
    },
    replace(url) {
      router.replace(url, url, { locale: consumerLocale })
    },
    back() {
      router.back()
    },
    locale: router?.locale as SupportedLocale,
    asPath: router?.asPath as Route,
    query: router?.query,
  }
}
